import { isAxiosError } from 'axios';

import { PageMessage } from '../../../../types/PageMessage';
import { ProductContext } from '../../../../types/ProductContext';
import { ProductMessage } from '../../../../types/ProductMessage';
import { ProductReferencesResponse, UpdateProductReferenceEntry } from '../../../../types/ProductReferenceMessage';
import { ProductUnit } from '../../../../types/ProductUnit';
import { authorizedAxiosInstance } from '../instances/AuthorizedAxiosInstance';

export interface GetProductsByQueryParams {
    searchQuery: string;
    includeArchived?: boolean;
    page?: number;
    supplierProductGroups?: string[];
}

export const getProductsByQuery = async ({
    searchQuery,
    includeArchived,
    page,
    supplierProductGroups,
}: GetProductsByQueryParams): Promise<PageMessage<ProductMessage>> => {
    const response = await authorizedAxiosInstance.get(`/v1/product`, {
        params: {
            page,
            searchQuery: searchQuery?.trim() || undefined,
            productTypes: 'LUBES',
            archived: !includeArchived ? false : undefined,
            supplierProductGroups: supplierProductGroups?.length ? supplierProductGroups : undefined,
        },
    });

    return response.data;
};

interface GetProductByNameParams {
    productName: string;
    fuzzy?: boolean;
}

export const getProductByName = async ({
    productName,
    fuzzy = false,
}: GetProductByNameParams): Promise<ProductMessage | null> => {
    try {
        const response = await authorizedAxiosInstance.get('/v2/products/name', {
            params: {
                productName,
                fuzzy,
            },
        });

        return response.data;
    } catch (error) {
        if (isAxiosError(error) && error.response?.status === 404) {
            return null;
        }

        throw error;
    }
};

export const getProductsByIds = async (productIds: string[]): Promise<ProductMessage[]> => {
    const productsListRequestMessage = {
        productTypes: ['LUBES'],
        productIds: productIds,
    };

    const response = await authorizedAxiosInstance.post(`/v3/products/list`, productsListRequestMessage);

    return response.data.products;
};

interface ProductMutationMessage {
    supplierProductGroup: string;
    name: string;
    packTypeDefault: string;
    unitDefault: ProductUnit;
    group: string;
    archived?: boolean;
}

export const getProductReferences = async (productId: string): Promise<ProductReferencesResponse> => {
    const response = await authorizedAxiosInstance.get(`/v1/product/${productId}/references`);
    return response.data;
};

interface UpdateProductReferencesParams {
    productId: string;
    references: UpdateProductReferenceEntry[];
}

export const updateProductReferences = async ({
    productId,
    references,
}: UpdateProductReferencesParams): Promise<ProductReferencesResponse> => {
    const response = await authorizedAxiosInstance.put(`/v1/product/${productId}/references`, { references });
    return response.data;
};

export const createProduct = async (product: ProductMutationMessage): Promise<ProductMessage> => {
    const response = await authorizedAxiosInstance.post(`/v1/product`, { ...product, type: ProductContext.LUBES });

    return response.data;
};

export const updateProduct = async ({
    product,
    id,
}: {
    product: ProductMutationMessage;
    id: string;
}): Promise<ProductMessage> => {
    const response = await authorizedAxiosInstance.put(`/v1/product/${id}`, { ...product, type: ProductContext.LUBES });

    return response.data;
};
